import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
  Rating,
  Switch,
  Divider,
  Avatar,
} from "@mui/material";
import ChecklistIcon from "@mui/icons-material/Checklist";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RoomIcon from "@mui/icons-material/Room";
import KitchenIcon from "@mui/icons-material/Kitchen";
import BathtubIcon from "@mui/icons-material/Bathtub";
import BedIcon from "@mui/icons-material/Bed";
import LivingIcon from "@mui/icons-material/Weekend";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import NoteIcon from "@mui/icons-material/Note";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TemplateIcon from "@mui/icons-material/Description";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import FeatureLayout from "./FeatureLayout";

// App screenshot placeholders
import checklistScreenshot1 from "../../../images/features/checklist-1.png";
import checklistScreenshot2 from "../../../images/features/checklist-2.png";
import checklistScreenshot3 from "../../../images/features/checklist-3.png";

const CustomChecklist = () => {
  const theme = useTheme();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const benefits = [
    {
      icon: <AssignmentIcon />,
      title: "Customizable Templates",
      description: "Create different checklists for each property. Studios, 2-bedrooms, or luxury villas - each gets the right cleaning standards.",
    },
    {
      icon: <RoomIcon />,
      title: "Room-by-Room Organization",
      description: "Organize tasks by room. Cleaners follow a logical flow through the property, ensuring nothing is missed.",
    },
    {
      icon: <PhotoCameraIcon />,
      title: "Photo Requirements",
      description: "Require photo proof for specific tasks. Verify that high-priority items were completed correctly.",
    },
    {
      icon: <NoteIcon />,
      title: "Task Instructions",
      description: "Add detailed notes, product recommendations, and special instructions for each cleaning task.",
    },
    {
      icon: <FileCopyIcon />,
      title: "Property-Specific Items",
      description: "Note special items like 'antique vase in living room' or 'fragile glass table' that need extra care.",
    },
    {
      icon: <TemplateIcon />,
      title: "Save as Templates",
      description: "Create master templates and apply to multiple properties. Update once, sync everywhere.",
    },
  ];

  const stats = [
    { value: "100%", label: "Task completion tracking" },
    { value: "Custom", label: "Per property" },
    { value: "Photo-ready", label: "Task verification" },
    { value: "Instant", label: "Template sync" },
  ];

  // Sample checklist data
  const sampleChecklist = {
    name: "Premium Villa Cleaning Standard",
    property: "Beachfront Villa - Unit 4B",
    rooms: [
      {
        name: "Kitchen",
        icon: <KitchenIcon />,
        tasks: [
          { task: "Wipe all countertops", requiresPhoto: false, notes: "Use disinfectant spray" },
          { task: "Clean inside microwave", requiresPhoto: false, notes: "Remove any food residue" },
          { task: "Scrub sink and faucet", requiresPhoto: true, notes: "Make sure it shines" },
          { task: "Clean refrigerator exterior", requiresPhoto: false, notes: "Wipe handles and doors" },
          { task: "Take out trash and recycling", requiresPhoto: false, notes: "Replace liners" },
          { task: "Sweep and mop floor", requiresPhoto: true, notes: "Pay attention to corners" },
        ],
      },
      {
        name: "Bathroom",
        icon: <BathtubIcon />,
        tasks: [
          { task: "Clean toilet inside and out", requiresPhoto: false, notes: "Use toilet bowl cleaner" },
          { task: "Scrub shower/tub", requiresPhoto: true, notes: "Remove soap scum and mildew" },
          { task: "Clean mirror and glass", requiresPhoto: false, notes: "Streak-free shine" },
          { task: "Wipe down vanity and sink", requiresPhoto: false, notes: "Don't forget the faucet" },
          { task: "Refill soap and toilet paper", requiresPhoto: false, notes: "Check supplies" },
          { task: "Sweep and mop floor", requiresPhoto: false, notes: "Get corners and behind toilet" },
        ],
      },
      {
        name: "Bedroom",
        icon: <BedIcon />,
        tasks: [
          { task: "Change all bed linens", requiresPhoto: false, notes: "Use fresh set from linen closet" },
          { task: "Dust all surfaces", requiresPhoto: false, notes: "Include nightstands and dressers" },
          { task: "Vacuum carpet/floor", requiresPhoto: true, notes: "Under bed and corners" },
          { task: "Wipe windowsills and blinds", requiresPhoto: false, notes: "Remove dust buildup" },
          { task: "Arrange pillows and throws", requiresPhoto: false, notes: "Make it photo-ready" },
          { task: "Check for guest belongings", requiresPhoto: false, notes: "Look in closets and drawers" },
        ],
      },
      {
        name: "Living Room",
        icon: <LivingIcon />,
        tasks: [
          { task: "Dust all surfaces", requiresPhoto: false, notes: "Shelves, TV stand, coffee table" },
          { task: "Vacuum upholstery", requiresPhoto: false, notes: "Couch cushions and chairs" },
          { task: "Clean TV screen", requiresPhoto: false, notes: "Use microfiber cloth" },
          { task: "Fluff and arrange pillows", requiresPhoto: false, notes: "Make it inviting" },
          { task: "Vacuum floor and rugs", requiresPhoto: true, notes: "Under furniture and corners" },
          { task: "Water plants", requiresPhoto: false, notes: "Only if requested" },
        ],
      },
      {
        name: "Special Items",
        icon: <CleaningServicesIcon />,
        tasks: [
          { task: "Antique vase in living room - gentle dust only", requiresPhoto: true, notes: "Do not move or lift" },
          { task: "French doors - clean glass", requiresPhoto: false, notes: "Avoid harsh chemicals on wood frame" },
          { task: "Outdoor patio - sweep", requiresPhoto: false, notes: "Check for debris" },
          { task: "Hot tub - check chemical levels", requiresPhoto: false, notes: "Report any issues" },
        ],
      },
    ],
  };

  const templateOptions = [
    { name: "Standard Short-Term Rental", rooms: 5, tasks: 32, popular: true },
    { name: "Luxury Villa Standard", rooms: 7, tasks: 48, popular: false },
    { name: "Studio/Efficiency", rooms: 3, tasks: 21, popular: false },
    { name: "Deep Clean Protocol", rooms: 6, tasks: 55, popular: false },
    { name: "Quick Turnover", rooms: 4, tasks: 18, popular: true },
  ];

  const handleOpenPreview = () => setPreviewOpen(true);
  const handleClosePreview = () => setPreviewOpen(false);
  const handleOpenTemplates = () => setTemplateDialogOpen(true);
  const handleCloseTemplates = () => setTemplateDialogOpen(false);

  return (
    <FeatureLayout
      title="Custom Cleaning Checklists"
      subtitle="Create perfect cleaning standards for every property"
      description="Stop guessing what was cleaned. Create custom, room-by-room checklists for each property. Cleaners follow your exact standards, take required photos, and leave detailed completion reports. Perfect for maintaining consistent quality across all your listings."
      icon={<ChecklistIcon sx={{ fontSize: 32 }} />}
      iconGradient="linear-gradient(135deg, #667eea, #764ba2)"
      breadcrumbs={[
        { label: "Features", link: "/features" },
        { label: "Custom Checklist" },
      ]}
      benefits={benefits}
      stats={stats}
      imageRight={checklistScreenshot1}
      imageLeft={checklistScreenshot2}
    >
      {/* How It Works */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Create Perfect Cleaning Standards
        </Typography>

        <Grid container spacing={4}>
          {[
            { step: "1", title: "Choose Template", desc: "Start from scratch or use a pre-built template", icon: "📋" },
            { step: "2", title: "Customize Tasks", desc: "Add, remove, or edit tasks for each room", icon: "✏️" },
            { step: "3", title: "Set Requirements", desc: "Mark tasks that need photo verification", icon: "📸" },
            { step: "4", title: "Add Notes", desc: "Include special instructions and product recommendations", icon: "📝" },
            { step: "5", title: "Assign to Property", desc: "Apply checklist to one or multiple properties", icon: "🏠" },
            { step: "6", title: "Cleaner Completes", desc: "Cleaner follows checklist and marks completion", icon: "✅" },
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "transform 0.2s",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.2rem",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="700">
                    {item.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Sample Checklist Preview */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight="800">
                Sample Checklist Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See how cleaners view and complete checklists
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={handleOpenPreview}
              sx={{ borderRadius: "50px" }}
            >
              View Full Preview
            </Button>
          </Box>

          <Box sx={{ p: 3, maxHeight: 400, overflow: "auto" }}>
            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              {sampleChecklist.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {sampleChecklist.property}
            </Typography>

            {sampleChecklist.rooms.map((room, roomIdx) => (
              <Paper
                key={roomIdx}
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {room.icon}
                  <Typography variant="subtitle2" fontWeight="700">
                    {room.name}
                  </Typography>
                  <Chip size="small" label={`${room.tasks.length} tasks`} sx={{ ml: 1 }} />
                </Box>
                <Box sx={{ p: 2 }}>
                  {room.tasks.slice(0, 3).map((task, taskIdx) => (
                    <Box key={taskIdx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {task.task}
                      </Typography>
                      {task.requiresPhoto && (
                        <Chip size="small" label="Photo required" icon={<PhotoCameraIcon />} variant="outlined" />
                      )}
                    </Box>
                  ))}
                  {room.tasks.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      +{room.tasks.length - 3} more tasks
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Template Library Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Ready-Made Templates
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              Don't start from scratch. Choose from professionally designed templates based on property type and cleaning frequency.
            </Typography>
            <List>
              {[
                "Standard short-term rental (32 tasks)",
                "Luxury villa protocol (48 tasks)",
                "Quick turnover checklist (18 tasks)",
                "Deep clean master list (55 tasks)",
                "Studio/efficiency template (21 tasks)",
              ].map((text, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              startIcon={<TemplateIcon />}
              onClick={handleOpenTemplates}
              sx={{ mt: 2, borderRadius: "50px" }}
            >
              Browse All Templates
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={checklistScreenshot3}
              alt="Checklist templates library"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Task Features */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Advanced Task Management
        </Typography>

        <Grid container spacing={3}>
          {[
            { icon: <PhotoCameraIcon />, title: "Photo Requirements", desc: "Require photo proof for specific tasks. Perfect for verifying high-priority items." },
            { icon: <NoteIcon />, title: "Detailed Instructions", desc: "Add notes, product recommendations, and special handling instructions per task." },
            { icon: <DragIndicatorIcon />, title: "Drag & Drop", desc: "Easily reorder tasks and rooms. Optimize cleaning flow for efficiency." },
            { icon: <FileCopyIcon />, title: "Duplicate Checklist", desc: "Copy checklists between properties. Save time on similar property types." },
            { icon: <EditIcon />, title: "Real-Time Updates", desc: "Update checklists anytime. Changes sync instantly to cleaner apps." },
            { icon: <CheckCircleIcon />, title: "Completion Tracking", desc: "See exactly which tasks were completed and when." },
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "transform 0.2s",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 32 } })}
                </Box>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Quality Control Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.dark, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight="800" gutterBottom>
                📊 Quality Control Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track checklist completion rates, identify frequently missed tasks, and ensure consistent quality across all properties. Receive alerts when tasks are incomplete or photos are missing.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Chip
                label="98% completion rate"
                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Property-Specific Items */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6} alignItems="center" direction={{ xs: "column-reverse", md: "row" }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Property-Specific Items
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              Every property has unique features. Note special items that need extra attention or care.
            </Typography>
            <List>
              {[
                "Mark fragile or valuable items needing gentle handling",
                "Note custom finishes or materials with special care requirements",
                "Flag problem areas from previous guest feedback",
                "Track missing or damaged inventory across stays",
              ].map((text, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                Example: Special Items Note
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: "italic", mb: 2 }}>
                "The blue vase on the mantel is an antique family heirloom worth $5,000. EXTREME CARE: Dust gently with provided soft cloth. Do not move or lift. Report any damage immediately."
              </Typography>
              <Chip size="small" label="High Priority" sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }} />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={handleCloseTemplates}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="800">Checklist Templates</Typography>
            <IconButton onClick={handleCloseTemplates}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose a template to get started. All templates are fully customizable.
          </Typography>
          <Grid container spacing={2}>
            {templateOptions.map((template, idx) => (
              <Grid item xs={12} key={idx}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="700">
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {template.rooms} rooms • {template.tasks} tasks
                    </Typography>
                    {template.popular && (
                      <Chip size="small" label="Popular" sx={{ ml: 1 }} />
                    )}
                  </Box>
                  <Button variant="outlined" size="small" startIcon={<FileCopyIcon />}>
                    Use Template
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<AddIcon />} variant="contained">
            Create From Scratch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "80vh" } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="800">Checklist Preview</Typography>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.02), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="subtitle1" fontWeight="700">{sampleChecklist.name}</Typography>
            <Typography variant="body2" color="text.secondary">{sampleChecklist.property}</Typography>
          </Box>
          
          <Box sx={{ p: 3, maxHeight: 500, overflow: "auto" }}>
            {sampleChecklist.rooms.map((room, roomIdx) => (
              <Paper
                key={roomIdx}
                variant="outlined"
                sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {room.icon}
                  <Typography variant="subtitle1" fontWeight="700">
                    {room.name}
                  </Typography>
                  <Chip size="small" label={`${room.tasks.length} tasks`} />
                </Box>
                <Box sx={{ p: 2 }}>
                  {room.tasks.map((task, taskIdx) => (
                    <Box key={taskIdx} sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                      <Checkbox size="small" disabled />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="500">
                          {task.task}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.notes}
                        </Typography>
                        {task.requiresPhoto && (
                          <Chip
                            size="small"
                            label="Photo required"
                            icon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button startIcon={<DownloadIcon />} variant="outlined">
            Export PDF
          </Button>
          <Button startIcon={<PrintIcon />} variant="outlined">
            Print
          </Button>
          <Button startIcon={<ShareIcon />} variant="contained">
            Apply to Property
          </Button>
        </DialogActions>
      </Dialog>
    </FeatureLayout>
  );
};

export default CustomChecklist;